import type { CitationDto } from '@/types/dto';

/**
 * Renders brief markdown to HTML, resolving [An] citation tokens into
 * <sup><a class="citation">[n]</a></sup> links. Direct TS port of the Blazor
 * BriefView.razor:172-194 logic so server and client stay visually identical.
 *
 * Inputs are escaped first, so untrusted markdown from the payload cannot
 * smuggle HTML. Output is a raw HTML string to be set via dangerouslySetInnerHTML.
 */
export function renderBriefMarkdown(
  md: string,
  citations: Record<string, CitationDto>
): string {
  if (!md || !md.trim()) return '';

  let html = escapeHtml(md);

  // **bold** → <strong>
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // ### and ## → <h3>. Matches only at line start.
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h3>$1</h3>');

  // - item → <li>, then wrap consecutive <li>…</li> runs in <ul>.
  html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
  html = html.replace(/(?:<li>.+?<\/li>\s*)+/gs, (m) => `<ul>${m}</ul>`);

  // [An] → <sup><a class="citation">[n]</a></sup>. Drops tokens with no matching citation,
  // matching the Blazor behavior (BriefView.razor:182-189).
  html = html.replace(/\[(A\d+)\]/g, (_, id: string) => {
    const src = citations[id];
    if (!src) return '';
    const n = id.slice(1);
    const title = escapeHtml(src.headline);
    const url = escapeHtml(src.sourceUrl);
    return `<sup><a class="citation" href="${url}" target="_blank" rel="noopener" title="${title}">[${n}]</a></sup>`;
  });

  // Paragraph split on blank lines; leave already-block content untouched.
  const paragraphs = html.split('\n\n').map((p) => {
    if (p.includes('<h3>') || p.includes('<ul>')) return p;
    return `<p>${p.replace(/\n/g, '<br/>')}</p>`;
  });
  return paragraphs.join('\n');
}

/** Returns the unique [An] citation ids referenced in a markdown string, in order of first appearance. */
export function collectCitedIds(md: string | null | undefined): string[] {
  if (!md) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const m of md.matchAll(/\[(A\d+)\]/g)) {
    const id = m[1];
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}
